// skipcq: JS-W1028
import { useCallback, useState } from 'react';
import { Outlet } from 'react-router-dom';

import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';

import { BrewerySessionProvider } from '../../context/BrewerySessionContext';
import BrewhousePageHeader, { BREWHOUSE_PORTAL_DRAWER_WIDTH } from '../brewhouse/BrewhousePageHeader';
import { BrewhousePageHeaderProvider } from '../brewhouse/BrewhousePageHeaderContext';
import { parchmentStack } from '../../utils/vintageSurfaces';
import BrewhousePortalSidebar from './BrewhousePortalSidebar';

function BrewhousePortalNavigation({ mobileOpen, onDrawerToggle, onMobileNavClose }) {
  return (
    <Box
      component="nav"
      sx={{ width: { sm: BREWHOUSE_PORTAL_DRAWER_WIDTH }, flexShrink: { sm: 0 } }}
      aria-label="Brewhouse portal"
    >
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: BREWHOUSE_PORTAL_DRAWER_WIDTH,
          },
        }}
      >
        <BrewhousePortalSidebar onNavigate={onMobileNavClose} />
      </Drawer>
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: BREWHOUSE_PORTAL_DRAWER_WIDTH,
          },
        }}
        open
      >
        <BrewhousePortalSidebar />
      </Drawer>
    </Box>
  );
}

function BrewhousePortalMain({ onDrawerToggle }) {
  const theme = useTheme();

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        width: { sm: `calc(100% - ${BREWHOUSE_PORTAL_DRAWER_WIDTH}px)` },
        minHeight: '100vh',
        ...parchmentStack(theme.palette.background.default),
      }}
    >
      <BrewhousePageHeader onDrawerToggle={onDrawerToggle} />
      <Box sx={{ minHeight: 0, overflow: 'auto' }}>
        <Outlet />
      </Box>
    </Box>
  );
}

BrewhousePortalMain.propTypes = {
  onDrawerToggle: PropTypes.func.isRequired,
};

function BrewhousePortalLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = useCallback(function handleDrawerToggle() {
    setMobileOpen((previous) => !previous);
  }, []);

  const handleMobileNavClose = useCallback(function handleMobileNavClose() {
    setMobileOpen(false);
  }, []);

  return (
    <BrewerySessionProvider>
      <BrewhousePageHeaderProvider>
        <Box sx={{ display: 'flex' }}>
          <BrewhousePortalNavigation
            mobileOpen={mobileOpen}
            onDrawerToggle={handleDrawerToggle}
            onMobileNavClose={handleMobileNavClose}
          />
          <BrewhousePortalMain onDrawerToggle={handleDrawerToggle} />
        </Box>
      </BrewhousePageHeaderProvider>
    </BrewerySessionProvider>
  );
}

export default BrewhousePortalLayout;
